'use client';

import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';

import type { Item } from '@/types/service-api';

import type { ComplaintArticleData } from './ComplaintArticleRow';
import { ComplaintArticleRow } from './ComplaintArticleRow';

interface ComplaintArticlesListProps {
  initialArticles?: ComplaintArticleData[];
  orderArticles?: Item[];
  articleFieldErrors?: Map<string, string[]>;
  onChange?: (articles: ComplaintArticleData[]) => void;
}

export function ComplaintArticlesList({
  initialArticles = [],
  orderArticles = [],
  articleFieldErrors,
  onChange,
}: ComplaintArticlesListProps) {
  const { t } = useTranslation('common');
  const [articles, setArticles] = useState<ComplaintArticleData[]>(
    initialArticles.length > 0 ? initialArticles : []
  );

  // Auto-populate from orderArticles when they become available
  useEffect(() => {
    if (
      orderArticles.length > 0 &&
      articles.length === 0 &&
      initialArticles.length === 0
    ) {
      const autoArticles: ComplaintArticleData[] = orderArticles.map((item) => ({
        id: crypto.randomUUID(),
        isSelected: false,
        position: item.itemNr || '',
        quantity: item.orderedAmount?.toString() || '',
        originalQuantity: item.orderedAmount?.toString() || '',
        model: item.model || '',
        articleNumber: item.articleNr || '',
        variant1: item.variant1 || '',
        variant2: item.variant2 || '',
        variant3: item.variant3 || '',
        variant4: item.variant4 || '',
        variant5: item.variant5 || '',
        articleDescription: item.description || '',
        errorDescription: '',
        priceReductionWished: false,
        completedByCustomer: false,
      }));
      setArticles(autoArticles);
      onChange?.(autoArticles);
    }
  }, [orderArticles, articles.length, initialArticles.length, onChange]);

  const handleArticleChange = (index: number, data: ComplaintArticleData) => {
    const newArticles = [...articles];
    newArticles[index] = data;
    setArticles(newArticles);
    onChange?.(newArticles);
  };

  const handleSelectionChange = (index: number, selected: boolean) => {
    const newArticles = [...articles];
    newArticles[index] = {
      ...newArticles[index],
      isSelected: selected,
      quantity: selected ? '' : newArticles[index].originalQuantity,
    };
    setArticles(newArticles);
    onChange?.(newArticles);
  };

  return (
    <div className="space-y-8">
      {/* Header with Title */}
      <div className="border-b border-black">
        <h3 className="text-h3Mobile uppercase lg:text-h3Desktop">
          {t('complaint.articles.select_title')}
        </h3>
      </div>

      {/* Empty State */}
      {articles.length === 0 && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
          <p className="text-p1CopyMobile text-gray-600 lg:text-p1CopyDesktop">
            {t('complaint.articles.no_articles')}
          </p>
        </div>
      )}

      {/* Article Rows */}
      {articles.map((article, index) => (
        <ComplaintArticleRow
          key={article.id}
          initialData={article}
          isSelected={article.isSelected}
          externalErrors={articleFieldErrors?.get(article.id)}
          onSelectionChange={(selected) => handleSelectionChange(index, selected)}
          onChange={(data) => handleArticleChange(index, data)}
        />
      ))}
    </div>
  );
}
